import { useState, useEffect } from 'react';
import DatePicker from '../DatePicker/DatePicker';
import { Button } from '../../ui/Button/Button';
import S from './ToolbarStyles';
import { TimeSelectionProps } from './types';
import {
  getStartOfTheDay,
  getStartOfWeek,
  getEndOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  convertSecToTimeFullLabel,
  getEndOfTheDay,
  getLocalTimezoneOffset,
} from '../../../utils/dateUtils';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetFleet from '../../../API/hooks/useGetFleetDetails';

const BUTTONS = ['Day', 'Week', 'Month'];
const BUTTONS_ALL = ['Day', 'Week', 'Month', 'All'];
const CUSTOM = 'Custom';

const PERIOD_TO_SELECTION: Record<string, string> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
  custom: 'Custom',
};

const getDefaultSelection = (filters: Record<string, string[] | string> | undefined, allTimeOption: boolean = false): string => {
  const buttons = allTimeOption ? BUTTONS_ALL : BUTTONS;
  if (!filters?.startDate?.[0] && !filters?.period[0]) {
    return buttons[2];
  }
  const period = filters?.period[0] as string;

  if (period && PERIOD_TO_SELECTION[period]) {
    const selection = PERIOD_TO_SELECTION[period];
    if (buttons.includes(selection)) {
      return selection;
    }
  }

  if (allTimeOption && filters?.startDate?.[0] === new Date(0).toISOString()) {
    return 'All';
  }

  return CUSTOM;
};

function TimeSelection({
  filters, setFilters, customTimeBlocked, allTimeOption,
}: TimeSelectionProps): JSX.Element {
  const [selection, setSelection] = useState(getDefaultSelection(filters, allTimeOption));
  const fleetId = useGetFleetId();
  const { data: fleetData } = useGetFleet(fleetId);

  const handleSelectionClick = (curSelection: string): void => {
    setSelection(curSelection);
    let startDate: Date;
    let endDate: Date | null;
    let period: string;
    if (curSelection === 'Day') {
      startDate = getStartOfTheDay();
      endDate = getEndOfTheDay();
      period = 'daily';
    } else if (curSelection === 'Week') {
      startDate = getStartOfWeek(fleetData?.settings?.weekStartDay);
      endDate = getEndOfWeek(fleetData?.settings?.weekStartDay);
      period = 'weekly';
    } else if (curSelection === 'Month') {
      startDate = getStartOfMonth();
      endDate = getEndOfMonth();
      period = 'monthly';
    } else {
      startDate = new Date(0);
      endDate = null;
      period = 'custom';
    }
    if (setFilters) {
      const newFilters = { ...filters };
      setFilters({
        ...newFilters,
        startDate: [startDate.toISOString()],
        endDate: endDate ? [endDate.toISOString()] : [],
        period: [period],
      });
    }
  };

  const handleApplyDate = (date: { startDate: Date; endDate: Date | null }): void => {
    const convertedStartDate = getStartOfTheDay(new Date(date.startDate.getTime() - getLocalTimezoneOffset().miliseconds));
    const convertedEndDate = date.endDate ? getEndOfTheDay(new Date(date.endDate.getTime() - getLocalTimezoneOffset().miliseconds)) : null;
    if (setFilters) {
      setFilters({
        ...filters,
        startDate: [convertedStartDate.toISOString()],
        ...(convertedEndDate ? { endDate: [convertedEndDate.toISOString()] } : null),
        period: ['custom'],
      });
    }
  };

  useEffect(() => {
    setSelection(getDefaultSelection(filters, allTimeOption));
  }, [filters?.period, filters?.startDate, filters?.endDate]);

  const buttons = allTimeOption ? BUTTONS_ALL : BUTTONS;

  const fromDate = filters?.startDate?.[0] ? new Date(filters?.startDate?.[0]) : getStartOfMonth();
  const toDate = filters?.endDate?.[0] ? new Date(filters?.endDate?.[0]) : getEndOfTheDay();
  const fromDateLabel = new Date(fromDate).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  const toDateLabel = new Date(toDate).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  const timePeriodLabel = convertSecToTimeFullLabel(Math.round((toDate.getTime() - fromDate.getTime()) / 1000), true);
  return (
    <S.InnerWrapper>
      <p className="selected-period">{`Selected period: ${fromDateLabel} - ${toDateLabel} (${timePeriodLabel} in total)`}</p>
      {
        buttons.map((item) => (
          <Button
            key={item}
            height="26px"
            width="72px"
            borderRadius="100px"
            padding="4px 16px"
            gap="4px"
            active={item === selection}
            onClick={() => handleSelectionClick(item)}
          ><p>{item}</p>
          </Button>
        ))
      }
      {(!customTimeBlocked) && (
        <DatePicker
          initialData={selection === CUSTOM ? {
            startDate: filters?.startDate?.[0] ? new Date(filters?.startDate?.[0]) : null,
            endDate: filters?.endDate?.[0] ? new Date(filters?.endDate?.[0]) : null,
          } : undefined}
          isActive={selection === CUSTOM}
          onSubmit={(data) => {
            handleSelectionClick(CUSTOM);
            handleApplyDate(data);
          }}
        />
      )}

    </S.InnerWrapper>
  );
}

export default TimeSelection;
